import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches render errors from the routed content region — most importantly a
 * failed lazy-chunk fetch.
 *
 * Route pages became dynamic `import()`s, which means navigation now performs a
 * network fetch that can fail *after* the app has loaded: a deploy rolls and the
 * hashed chunk filename 404s against a stale index.html, or the connection drops
 * mid-navigation. React surfaces that as a thrown error during render, and with
 * nothing to catch it the whole tree unmounts — the operator gets a white screen
 * with no way back. Eagerly-bundled pages had no post-load fetch, so this
 * failure mode did not previously exist.
 *
 * Scope is deliberately the content region, not the app: the sidebar and topbar
 * stay interactive, so an operator whose Settings chunk failed can still click
 * through to a route whose chunk is already warm.
 *
 * There is no hook equivalent for `componentDidCatch`, so this is a class.
 */

interface Props {
  children: ReactNode;
  /**
   * Changing this resets the boundary. `AppShell` passes the pathname, so
   * navigating away from a failed route clears the error instead of pinning the
   * content region to it — without this, the boundary latches and every
   * subsequent navigation renders the stale error.
   */
  resetKey?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * A dynamic `import()` that fails to fetch rejects with a plain Error whose
 * message differs per bundler and browser, so there is no error class to
 * match on. These are the phrasings Vite, Rollup and the browsers actually
 * produce; anything else is treated as a real render fault, which is the safer
 * way round — mislabelling a bug as a network blip is the failure that hides
 * bugs, not the other way round.
 */
function isChunkLoadFailure(error: Error): boolean {
  const message = `${error.name}: ${error.message}`;
  return /dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed|error loading dynamically imported/i
    .test(message);
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };
  private resetKeyAtCapture: string | undefined;

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.resetKeyAtCapture = this.props.resetKey;
    this.props.onError?.(error, info);
  }

  componentDidUpdate(): void {
    // Reset on navigation away from the route that failed. Comparing against
    // the value captured at throw time (rather than the previous prop) keeps
    // this correct when the error and the navigation land in the same commit.
    if (this.state.error && this.props.resetKey !== this.resetKeyAtCapture) {
      this.resetKeyAtCapture = undefined;
      this.setState({ error: null });
    }
  }

  private retry = (): void => {
    // A failed dynamic import is cached as a rejected module by the bundler
    // runtime, so simply clearing the error re-renders straight back into the
    // same rejection. A reload is what actually re-fetches the chunk — and it
    // also picks up the newer index.html in the rolled-deploy case, which is
    // the most likely cause of the 404 in the first place.
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    // Two very different failures land here and they deserve different copy.
    // A failed chunk fetch really is transient, and reloading really does fix
    // it. A render fault does not: telling an operator "the connection
    // dropped" sends them to reload forever and costs us the bug report we
    // actually need. Match on the shape browsers use for a rejected dynamic
    // import rather than assuming every error is the network's fault.
    const transient = isChunkLoadFailure(error);
    return (
      <section role="alert" className="route-error" data-route-error="true">
        <h2 className="route-error-title">
          {transient ? "This page didn’t load" : "Something went wrong on this page"}
        </h2>
        <p className="route-error-body">
          {transient
            ? "The connection dropped, or this page was updated while you were working. Reloading usually fixes it."
            : "This page hit an error and stopped rendering. Reloading is worth a try; if it keeps happening, send us the message below and we can fix it."}
          {" "}Your other pages still work — you can keep using the sidebar.
        </p>
        {!transient && error.message ? (
          <p className="route-error-detail">{error.message}</p>
        ) : null}
        <button type="button" className="btn" onClick={this.retry}>
          Reload this page
        </button>
      </section>
    );
  }
}
